import { useTranslation } from 'react-i18next';

const infraImg = 'http://geel.fp.08wx.cn/images/2f5b5843382506f555286df0a544ac80.jpg';

type LocaleKey = 'zh-CN' | 'en' | 'zh-TW';

const COPY = {
  'zh-CN': {
    badge: 'Infrastructure',
    title1: '世界级机房基础设施',
    title2: '稳定、高效、绿色',
    desc: '在冰岛、哈萨克斯坦、加拿大、阿联酋、巴拉圭建有五大战略机房，总装机容量超 1.55GW，采用 100% 清洁能源，液冷浸没技术确保矿机在最佳工况下持续稳定运行。',
    cardTitle: '机房基础设施',
    cardMeta: '全球 5 大节点 · 1.55GW 总装机',
    dcTitle: '全球机房节点',
    metrics: [
      { val: '1.2', unit: 'PUE', label: '超低能耗比' },
      { val: '100%', unit: '', label: '清洁能源占比' },
      { val: '99.95%', unit: '', label: '电力 SLA' },
    ],
    infraFeatures: [
      { icon: 'ri-leaf-line', title: '绿色清洁能源', desc: '水电 / 风电 / 太阳能优先选址，PUE 低至 1.2，碳中和矿场认证' },
      { icon: 'ri-temp-cold-line', title: '液冷浸没散热', desc: '全浸没液冷技术，矿机寿命延长 30%，噪音降低 80%，密度翻倍' },
      { icon: 'ri-wifi-line', title: '多链路冗余网络', desc: '运营商 BGP 多线路，延迟 <10ms 到全球主要矿池，上行带宽 10Gbps+' },
      { icon: 'ri-shield-check-line', title: '7×24 安保运维', desc: '全天候武装安保，双因素门禁系统，远程实时视频监控' },
      { icon: 'ri-flashlight-line', title: '独立 UPS 电源', desc: '双路市电 + 柴油发电机备用，电力 SLA 99.95%，零宕机保障' },
      { icon: 'ri-global-line', title: '全球五大机房', desc: '冰岛 · 哈萨克斯坦 · 加拿大 · 阿联酋 · 巴拉圭，就近入驻可选' },
    ],
    datacenters: [
      { location: '冰岛 · 雷克雅未克', energy: '地热+水电', power: '200MW', temp: '年均 5°C', status: '运营中' },
      { location: '哈萨克斯坦 · 阿拉木图', energy: '水电', power: '500MW', temp: '年均 10°C', status: '运营中' },
      { location: '加拿大 · 魁北克', energy: '水电', power: '300MW', temp: '年均 6°C', status: '运营中' },
      { location: '阿联酋 · 阿布扎比', energy: '太阳能', power: '150MW', temp: '受控机房', status: '扩建中' },
      { location: '巴拉圭 · 亚松森', energy: '水电', power: '400MW', temp: '年均 25°C', status: '运营中' },
    ],
    runningStatus: '运营中',
  },
  en: {
    badge: 'Infrastructure',
    title1: 'World-Class Mining Infrastructure',
    title2: 'Stable, Efficient, and Green',
    desc: 'Five strategic mining data centers across Iceland, Kazakhstan, Canada, UAE, and Paraguay with over 1.55GW installed capacity. Built on 100% clean energy and immersion cooling for reliable operation under optimal conditions.',
    cardTitle: 'Infrastructure',
    cardMeta: '5 Global Nodes · 1.55GW Total Capacity',
    dcTitle: 'Global Data Center Nodes',
    metrics: [
      { val: '1.2', unit: 'PUE', label: 'Ultra-Low PUE' },
      { val: '100%', unit: '', label: 'Clean Energy Share' },
      { val: '99.95%', unit: '', label: 'Power SLA' },
    ],
    infraFeatures: [
      { icon: 'ri-leaf-line', title: 'Green Clean Energy', desc: 'Hydro/wind/solar-priority locations with PUE as low as 1.2 and carbon-neutral certification.' },
      { icon: 'ri-temp-cold-line', title: 'Immersion Liquid Cooling', desc: 'Full immersion cooling extends miner lifespan by 30%, cuts noise by 80%, and doubles density.' },
      { icon: 'ri-wifi-line', title: 'Redundant Multi-Link Network', desc: 'Carrier-grade BGP multi-line networking with <10ms latency to major pools and 10Gbps+ uplink.' },
      { icon: 'ri-shield-check-line', title: '24/7 Security & Operations', desc: 'Round-the-clock security, dual-factor access control, and remote real-time video monitoring.' },
      { icon: 'ri-flashlight-line', title: 'Independent UPS Power', desc: 'Dual utility feeds with diesel backup, 99.95% power SLA, and zero-downtime assurance.' },
      { icon: 'ri-global-line', title: 'Five Global Facilities', desc: 'Iceland, Kazakhstan, Canada, UAE, and Paraguay with flexible nearby deployment options.' },
    ],
    datacenters: [
      { location: 'Iceland · Reykjavik', energy: 'Geothermal + Hydro', power: '200MW', temp: 'Avg 5°C', status: 'Live' },
      { location: 'Kazakhstan · Almaty', energy: 'Hydro', power: '500MW', temp: 'Avg 10°C', status: 'Live' },
      { location: 'Canada · Quebec', energy: 'Hydro', power: '300MW', temp: 'Avg 6°C', status: 'Live' },
      { location: 'UAE · Abu Dhabi', energy: 'Solar', power: '150MW', temp: 'Controlled Facility', status: 'Expanding' },
      { location: 'Paraguay · Asuncion', energy: 'Hydro', power: '400MW', temp: 'Avg 25°C', status: 'Live' },
    ],
    runningStatus: 'Live',
  },
  'zh-TW': {
    badge: 'Infrastructure',
    title1: '世界級機房基礎設施',
    title2: '穩定、高效、綠色',
    desc: '在冰島、哈薩克、加拿大、阿聯酋、巴拉圭建有五大戰略機房，總裝機容量超過 1.55GW，採用 100% 清潔能源，液冷浸沒技術確保礦機在最佳工況下持續穩定運行。',
    cardTitle: '機房基礎設施',
    cardMeta: '全球 5 大節點 · 1.55GW 總裝機',
    dcTitle: '全球機房節點',
    metrics: [
      { val: '1.2', unit: 'PUE', label: '超低能耗比' },
      { val: '100%', unit: '', label: '清潔能源占比' },
      { val: '99.95%', unit: '', label: '電力 SLA' },
    ],
    infraFeatures: [
      { icon: 'ri-leaf-line', title: '綠色清潔能源', desc: '水電 / 風電 / 太陽能優先選址，PUE 低至 1.2，碳中和礦場認證' },
      { icon: 'ri-temp-cold-line', title: '液冷浸沒散熱', desc: '全浸沒液冷技術，礦機壽命延長 30%，噪音降低 80%，密度翻倍' },
      { icon: 'ri-wifi-line', title: '多鏈路冗餘網路', desc: '運營商 BGP 多線路，延遲 <10ms 到全球主要礦池，上行帶寬 10Gbps+' },
      { icon: 'ri-shield-check-line', title: '7×24 安保運維', desc: '全天候武裝安保，雙因素門禁系統，遠端即時影像監控' },
      { icon: 'ri-flashlight-line', title: '獨立 UPS 電源', desc: '雙路市電 + 柴油發電機備援，電力 SLA 99.95%，零宕機保障' },
      { icon: 'ri-global-line', title: '全球五大機房', desc: '冰島 · 哈薩克斯坦 · 加拿大 · 阿聯酋 · 巴拉圭，就近入駐可選' },
    ],
    datacenters: [
      { location: '冰島 · 雷克雅未克', energy: '地熱+水電', power: '200MW', temp: '年均 5°C', status: '運營中' },
      { location: '哈薩克斯坦 · 阿拉木圖', energy: '水電', power: '500MW', temp: '年均 10°C', status: '運營中' },
      { location: '加拿大 · 魁北克', energy: '水電', power: '300MW', temp: '年均 6°C', status: '運營中' },
      { location: '阿聯酋 · 阿布達比', energy: '太陽能', power: '150MW', temp: '受控機房', status: '擴建中' },
      { location: '巴拉圭 · 亞松森', energy: '水電', power: '400MW', temp: '年均 25°C', status: '運營中' },
    ],
    runningStatus: '運營中',
  },
};

export default function MiningInfraSection() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'zh-CN').toLowerCase();
  const locale: LocaleKey = lang.startsWith('zh-tw') || lang.startsWith('zh-hk') ? 'zh-TW' : lang.startsWith('en') ? 'en' : 'zh-CN';
  const copy = COPY[locale];

  return (
    <section id="infrastructure" className="bg-light-section py-24">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-14 xl:gap-20">
          <div className="lg:w-[420px] xl:w-[500px] flex-shrink-0">
            <div className="relative rounded-2xl overflow-hidden border border-primary/15 sticky top-24">
              <div className="w-full h-[300px]">
                <img src={infraImg} alt="Mining Infrastructure" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
              </div>
              <div className="absolute top-4 left-4 bg-white/95 border border-primary/20 rounded-xl px-4 py-3 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-server-line text-primary text-sm" />
                  <span className="text-gray-800 text-xs font-medium">{copy.cardTitle}</span>
                </div>
                <div className="text-gray-400 text-xs">{copy.cardMeta}</div>
              </div>
              <div className="p-4 bg-white">
                <div className="text-xs text-gray-400 mb-3">{copy.dcTitle}</div>
                <div className="space-y-2">
                  {copy.datacenters.map((dc, i) => (
                    <div key={i} className="flex items-center justify-between bg-light-alt rounded-lg px-3 py-2">
                      <div>
                        <div className="text-gray-800 text-xs font-medium">{dc.location}</div>
                        <div className="text-gray-400 text-xs">{dc.energy} · {dc.power}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${dc.status === copy.runningStatus ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-accent/10 text-accent border border-accent/20'}`}>
                        {dc.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <i className="ri-server-line text-primary text-xs" />
              <span className="text-primary text-xs font-medium uppercase tracking-widest">{copy.badge}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 leading-tight">
              {copy.title1}
              <br />
              <span className="text-primary">{copy.title2}</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">{copy.desc}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {copy.infraFeatures.map((f, i) => (
                <div key={i} className="bg-white border border-primary/10 rounded-xl p-4 hover:border-primary/25 transition-all duration-200">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary/10 flex-shrink-0 mt-0.5">
                      <i className={`${f.icon} text-sm text-primary`} />
                    </div>
                    <div>
                      <h4 className="text-gray-800 text-sm font-medium mb-1">{f.title}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {copy.metrics.map((m, i) => (
                <div key={i} className="bg-white border border-primary/10 rounded-xl p-4 text-center">
                  <div className="text-xl font-heading font-bold text-primary">{m.val}<span className="text-xs ml-0.5 text-gray-400">{m.unit}</span></div>
                  <div className="text-gray-400 text-xs mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
